import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Upload, FileCheck, GraduationCap, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const currentYear = new Date().getFullYear();
const VALID_GRAD_YEARS = [currentYear, currentYear - 1, currentYear - 2];

/**
 * Modal that collects a student ID or graduation certificate so a new member
 * can subscribe to the Student & New Grad tier. On success, calls
 * `onEligibilityReady(docUrl)` so the parent can proceed with /payments/subscribe.
 */
const StudentEligibilityDialog = ({ open, onOpenChange, onEligibilityReady }) => {
  const [eligibilityType, setEligibilityType] = useState("student"); // "student" | "new_grad"
  const [gradYear, setGradYear] = useState(currentYear);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [existingStatus, setExistingStatus] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Pre-fetch any prior submission so we can surface "Already submitted, awaiting review"
    axios.get(`${API}/users/eligibility-status`).then(r => {
      if (r.data?.status && r.data.status !== "none") setExistingStatus(r.data);
    }).catch(() => {});
  }, [open]);

  const reset = () => {
    setFile(null);
    setEligibilityType("student");
    setGradYear(currentYear);
    setExistingStatus(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please attach your student ID or graduation certificate");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large — max 10 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("eligibility_type", eligibilityType);
      if (eligibilityType === "new_grad") fd.append("grad_year", String(gradYear));
      const r = await axios.post(`${API}/users/upload-eligibility-doc`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document received. Continuing to checkout...");
      reset();
      onOpenChange(false);
      onEligibilityReady?.(r.data.url);
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-md" data-testid="student-eligibility-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center mb-3">
            <GraduationCap className="w-6 h-6 text-[#8B5CF6]" />
          </div>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl">
            Verify student eligibility
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            The Student & New Grad tier is for current students or RDHs within their first 2 years of practice.
          </DialogDescription>
        </DialogHeader>

        {existingStatus?.status === "submitted" ? (
          <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#D97706] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#6B4E8A]">Already submitted</p>
              <p className="text-xs text-[#64748B] mt-1">
                Your previous submission ({existingStatus.type === "new_grad" ? `New Grad ${existingStatus.grad_year}` : "Student"}) is awaiting admin review. You can replace it below if needed.
              </p>
              <Button
                variant="link"
                size="sm"
                className="text-[#8DB5A0] px-0 h-auto mt-2"
                onClick={() => setExistingStatus(null)}
                data-testid="replace-eligibility-btn"
              >
                Replace document
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Type toggle */}
            <div className="grid grid-cols-2 gap-3" role="radiogroup" data-testid="eligibility-type-toggle">
              {[
                { key: "student", label: "Current student", desc: "Enrolled in a dental hygiene program" },
                { key: "new_grad", label: "New grad", desc: "Graduated within last 2 years" },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setEligibilityType(opt.key)}
                  className={`text-left p-3 rounded-xl border transition-all ${
                    eligibilityType === opt.key
                      ? "border-[#8B5CF6] bg-[#8B5CF6]/5"
                      : "border-[#E5DFEC] hover:border-[#94A3B8]"
                  }`}
                  data-testid={`eligibility-type-${opt.key}`}
                >
                  <p className="text-sm font-medium text-[#6B4E8A]">{opt.label}</p>
                  <p className="text-[10px] text-[#64748B] mt-0.5 leading-tight">{opt.desc}</p>
                </button>
              ))}
            </div>

            {/* Grad year — only for new grads */}
            {eligibilityType === "new_grad" && (
              <div>
                <Label htmlFor="grad-year" className="text-sm font-medium text-[#6B4E8A] mb-2 block">
                  Year of graduation
                </Label>
                <select
                  id="grad-year"
                  value={gradYear}
                  onChange={(e) => setGradYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-[#E5DFEC] rounded-md bg-white text-sm text-[#6B4E8A] focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/30"
                  data-testid="grad-year-select"
                >
                  {VALID_GRAD_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}

            {/* File upload */}
            <div>
              <Label className="text-sm font-medium text-[#6B4E8A] mb-2 block">
                {eligibilityType === "student" ? "Student ID or enrollment letter" : "Graduation certificate or diploma"}
              </Label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-[#E5DFEC] hover:border-[#8B5CF6] rounded-xl p-6 transition-colors text-center"
                data-testid="eligibility-file-picker"
              >
                {file ? (
                  <div className="flex flex-col items-center gap-1">
                    <FileCheck className="w-7 h-7 text-[#10B981]" />
                    <p className="text-sm font-medium text-[#6B4E8A] truncate max-w-full">{file.name}</p>
                    <p className="text-xs text-[#64748B]">{(file.size / 1024).toFixed(0)} KB · click to change</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <Upload className="w-7 h-7 text-[#94A3B8]" />
                    <p className="text-sm font-medium text-[#6B4E8A]">Click to upload</p>
                    <p className="text-xs text-[#64748B]">JPG, PNG, or PDF · max 10 MB</p>
                  </div>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="eligibility-file-input"
              />
            </div>

            <p className="text-xs text-[#64748B] leading-relaxed">
              Documents are reviewed manually. False submissions will result in immediate ban and may be reported to your provincial regulator.
            </p>
          </>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
            data-testid="eligibility-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={uploading || (!file && !existingStatus)}
            className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
            data-testid="eligibility-submit-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
            ) : existingStatus?.status === "submitted" && !file ? (
              "Continue with prior document"
            ) : (
              "Submit & Continue to Checkout"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentEligibilityDialog;
